import { useState } from "react";
import { Search } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const EVENTS = [
  {
    id: "first-event",
    name: "Trona — First Event",
    date: "2026-10-09",
    dateLabel: "09 October 2026",
    dateChip: "Fri 09 Oct 2026",
    time: "9PM → Till End",
    venue: "Vapore 1928 — Fabbrica del Vapore",
    venueShort: "@Vapore 1928 / Fabbrica del Vapore",
    entry: "Free entry — Limited capacity",
    artists: "Daniele Griffo, THAT-ONE",
    poster: "/event-first-poster.webp",
    ticketUrl: "https://link.dice.fm/S83d4a6d4869",
  },
];

export default function Events() {
  const [view, setView] = useState("all");
  const [query, setQuery] = useState("");

  const now = new Date();
  const filtered = EVENTS.filter((ev) => {
    const isUpcoming = new Date(ev.date) >= now;
    if (view === "upcoming" && !isUpcoming) return false;
    if (view === "past" && isUpcoming) return false;
    const q = query.trim().toLowerCase();
    return (
      !q ||
      `${ev.name} ${ev.dateLabel} ${ev.venue} ${ev.artists}`
        .toLowerCase()
        .includes(q)
    );
  });

  return (
    <main
      className="px-6 md:px-12 w-full max-w-6xl mx-auto pt-10 md:pt-16 flex-1"
      data-testid="events-page"
    >
      <Seo
        title="Events — TRONA"
        siteName="TRONA"
        description="Upcoming TRONA nights in Milan. Dates, venues and artists."
      />
      <Reveal>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-none tracking-tight text-[#1A1512]">
          Events
        </h1>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="events-filters">
          <select
            value={view}
            onChange={(e) => setView(e.target.value)}
            data-testid="events-view-select"
            aria-label="View"
            className="appearance-none bg-transparent border border-[#1A1512] rounded-full pl-5 pr-8 py-2 font-mono text-xs tracking-[0.2em] uppercase text-[#1A1512] cursor-pointer"
          >
            <option value="all">All</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
          </select>
          <label className="flex items-center gap-2 border border-[#1A1512] rounded-full px-5 py-2">
            <Search size={13} strokeWidth={2} aria-hidden="true" className="text-[#1A1512]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              data-testid="events-search-input"
              aria-label="Search events"
              className="bg-transparent outline-none font-mono text-xs tracking-[0.2em] uppercase text-[#1A1512] placeholder:text-[#1A1512]/50 w-32 md:w-48"
            />
          </label>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-10 border-t border-[#1A1512]" data-testid="events-structure">
          {filtered.length === 0 ? (
            EVENTS.length === 0 ? (
              <div
                className="mt-10 rounded-xl bg-[#1A1512] text-[#efe2c2] px-6 py-16 md:py-20 text-center"
                data-testid="events-empty-state"
              >
                <p className="font-display text-2xl sm:text-3xl uppercase tracking-tight">
                  Coming soon
                </p>
                <p className="mt-3 font-mono font-light text-xs sm:text-sm tracking-[0.15em] uppercase text-[#efe2c2]/70">
                  Stay tuned — dates announced shortly
                </p>
              </div>
            ) : (
              <p
                className="py-10 font-mono text-xs tracking-[0.2em] uppercase text-[#504439]"
                data-testid="events-no-results"
              >
                No results — try another search
              </p>
            )
          ) : (
            <div className="flex flex-wrap gap-x-12 gap-y-4">
              {filtered.map((ev) => (
                <a
                  key={ev.id}
                  href={ev.ticketUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`event-${ev.id}`}
                  aria-label={`${ev.name} — ${ev.dateLabel}, ${ev.venue}. Get tickets.`}
                  className="group block w-full max-w-[300px] py-12 border-b border-[#1A1512]"
                >
                  <img
                    src={ev.poster}
                    alt={`${ev.name} — poster`}
                    className="w-full h-auto border border-[#1A1512]/20 transition-opacity duration-150 group-hover:opacity-90"
                    data-testid="event-poster"
                  />
                  <div className="mt-5">
                    <div className="flex flex-wrap gap-2">
                      <span
                        className="inline-block whitespace-nowrap bg-[#F5F1E8] border border-[#1A1512]/20 rounded-md px-3 py-1.5 font-mono text-[11px] tracking-[0.15em] uppercase text-[#1A1512]"
                        data-testid="event-date-chip"
                      >
                        {ev.dateChip}
                      </span>
                      <span
                        className="inline-block whitespace-nowrap bg-[#F5F1E8] border border-[#1A1512]/20 rounded-md px-3 py-1.5 font-mono text-[11px] tracking-[0.15em] uppercase text-[#1A1512]"
                        data-testid="event-entry-chip"
                      >
                        Free entry
                      </span>
                    </div>
                    <p
                      className="mt-3 font-mono text-xs sm:text-sm tracking-[0.15em] uppercase text-[#504439]"
                      data-testid="event-time"
                    >
                      {ev.time}
                    </p>
                    <h2 className="mt-2 font-mono font-bold text-base sm:text-lg leading-snug tracking-[0.06em] uppercase text-[#1A1512] group-hover:underline underline-offset-4 decoration-1">
                      {ev.name}{" "}
                      <span className="text-[#504439]">{ev.venueShort}</span>
                    </h2>
                    <span
                      data-testid="event-ticket-cta"
                      className="mt-5 inline-block self-start bg-[#1A1512] text-[#efe2c2] rounded-full px-6 py-2.5 font-mono text-[11px] tracking-[0.25em] uppercase group-hover:bg-[#504439] transition-colors duration-150"
                    >
                      Grab your ticket now
                    </span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </Reveal>
    </main>
  );
}
